import { use_effect } from '@/hooks/_base/use_effect';
import { cn } from '@/utils/utils_client';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { DTO__number_range } from '@shared/dto/generation/settings/DTO__number_range';
import { forwardRef, useMemo, useState } from 'react';
import { SliderMark } from './slider_mark';
import { SliderMarks } from './slider_marks';
import { SliderMarksTemporary } from './slider_marks_temporary';
import { UI_PRIMITIVE__slider_thumb_static } from './UI_PRIMITIVE__slider_thumb_static';

// Types
interface SliderProps extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  marks?: SliderMark[];
  max: number;
  min: number;
  step?: number;
  value: number[];
  onValueChange: (value: number[]) => void;
  onValueCommit: (value: number[]) => void;
  value_hovered?: number;
  value_starred?: number;
  defaultValue?: number[];
}

export const UI_PRIMITIVE__slider = forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      marks = [],
      max,
      min,
      step = 1,
      defaultValue,
      value,
      onValueChange,
      onValueCommit,
      value_hovered = NaN,
      value_starred = NaN,
      disabled = false,
      ...props
    },
    ref,
  ) => {
    defaultValue = defaultValue ?? [];
    /*
     * FIELDS
     */

    const [show_temporary_marks, set_show_temporary_marks] = useState(false);

    const [grabbing, set_grabbing] = useState(false);

    const [value_current, set_value_current] = useState<number>(value[0]);

    const percent = useMemo(() => get_percent(), [value_current, min, max]);
    /*
     * EFFECTS
     */

    use_effect(() => set_value_current(value[0]), [value]);

    /*
     * METHODS
     */
    function get_percent() {
      let range = new DTO__number_range(min, max);

      let value = range.clamp(value_current);

      return range.value_to_percentage(value);
    }

    /*
     * RENDERS
     */

    function render_thumb() {
      if (show_temporary_marks) return null;

      return (
        <div
          onPointerDown={() => set_grabbing(true)}
          onPointerUp={() => set_grabbing(false)}
          style={{ left: `calc(${percent}%)` }}
          className={`absolute ui-primitive-slider-thumb 
                      translate-x-[-50%]
                      flex-center
                      px-1
                      min-w-8 min-h-4
                      bg-bar rounded-lg   
                      border border-component-item-border
                      text-xs font-medium text-primary
                      transition duration-200 hover-animation
                      disabled:pointer-events-none disabled:opacity-50
                      focus-visible:outline-none
                      ${disabled ? '' : 'cursor-grab'} 
                      ${grabbing && '!cursor-grabbing'}
                    `}
        >
          {value_current}
        </div>
      );
    }

    function render_thumb_hovered() {
      if (isNaN(value_hovered)) return null;
      if (show_temporary_marks) return null;

      return <UI_PRIMITIVE__slider_thumb_static value={value_hovered} {...{ min, max }} />;
    }

    /*
     * HTML
     */
    return (
      <>
        <SliderPrimitive.Root
          className={cn(
            `           ui-primitive-slider-root 
                      relative flex items-center
                      w-full 
                      touch-none select-none `,
            className,
          )}
          value={[value_current]}
          {...{ ref, step, min, max, defaultValue, onValueChange, onValueCommit, disabled }}
          {...props}
        >
          <SliderPrimitive.Track
            className={`ui-primitive-slider-track 
                      relative grow overflow-hidden
                      h-2 w-full 
                      cursor-pointer 
                      bg-component rounded-full 
                      border border-1 
                      ${show_temporary_marks ? 'border-component-item-border' : 'border-component-item'} `}
          >
            <SliderPrimitive.Range
              className={`ui-primitive-slider-range absolute cursor-pointer h-full bg-component-item duration-300 transition-opacity ${show_temporary_marks ? 'opacity-0' : 'opacity-100'}`}
            />
          </SliderPrimitive.Track>
          <SliderMarks {...{ marks, max, step }} />

          <SliderMarksTemporary {...{ min, max, step, set_show_temporary_marks, show_temporary_marks }} />

          {render_thumb()}

          {render_thumb_hovered()}
        </SliderPrimitive.Root>
      </>
    );
  },
);
